import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useTablets } from '@/lib/api/tablet';
import {
  deleteTabletGroup,
  setAllTabletGroupsEnabled,
  updateTabletGroup,
  useTabletGroups,
} from '@/lib/api/tabletGroup';
import { TabletGroup } from '@/lib/schema/tabletGroup';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { toast } from 'sonner';
import { columns, TabletGroupsTableMeta } from './columns';
import { GroupFormDialog } from './groupFormDialog';

export function TabletGroups() {
  const [groups] = useTabletGroups();
  const [tablets] = useTablets();

  const [formOpen, setFormOpen] = useState(false);
  const [groupToEdit, setGroupToEdit] = useState<TabletGroup | null>(null);
  const [groupToDelete, setGroupToDelete] = useState<TabletGroup | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const memberCountByGroupId = useMemo(() => {
    const counts = new Map<string, number>();
    (tablets || []).forEach((tablet) => {
      if (tablet.groupId) {
        counts.set(tablet.groupId, (counts.get(tablet.groupId) || 0) + 1);
      }
    });
    return counts;
  }, [tablets]);

  const handleToggleAll = async (enabled: boolean) => {
    const groupIds = (groups || [])
      .filter((g) => g.id && g.enabled !== enabled)
      .map((g) => g.id!);
    if (!groupIds.length) return;
    try {
      await setAllTabletGroupsEnabled(groupIds, enabled);
      toast.success(
        `${groupIds.length} group(s) ${enabled ? 'enabled' : 'disabled'}`
      );
    } catch (error) {
      console.error('Error toggling all tablet groups:', error);
      toast.error('Failed to update groups');
    }
  };

  const handleToggle = async (group: TabletGroup, enabled: boolean) => {
    if (!group.id) return;
    try {
      await updateTabletGroup(group.id, { enabled });
      toast.success(`${group.name} ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error toggling tablet group:', error);
      toast.error('Failed to update group');
    }
  };

  const handleDelete = async () => {
    if (!groupToDelete?.id) return;

    setIsDeleting(true);
    try {
      const memberIds = (tablets || [])
        .filter((t) => t.id && t.groupId === groupToDelete.id)
        .map((t) => t.id!);
      await deleteTabletGroup(groupToDelete.id, memberIds);
      toast.success('Group deleted');
      setGroupToDelete(null);
    } catch (error) {
      console.error('Error deleting tablet group:', error);
      toast.error('Failed to delete group');
    } finally {
      setIsDeleting(false);
    }
  };

  const table = useReactTable({
    data: (groups || []) as TabletGroup[],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      sorting: [{ id: 'name', desc: false }],
    },
    meta: {
      memberCountByGroupId,
      onToggle: handleToggle,
      onEdit: (group) => {
        setGroupToEdit(group);
        setFormOpen(true);
      },
      onDelete: setGroupToDelete,
    } satisfies TabletGroupsTableMeta,
  });

  return (
    <div className="w-full pb-4">
      <div className="flex justify-between items-center pb-6">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleToggleAll(true)}
            disabled={!groups?.some((g) => !g.enabled)}
          >
            Enable all
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleToggleAll(false)}
            disabled={!groups?.some((g) => g.enabled)}
          >
            Disable all
          </Button>
        </div>
        <Button
          type="button"
          onClick={() => {
            setGroupToEdit(null);
            setFormOpen(true);
          }}
        >
          New
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={header.id === 'actions' ? 'w-px' : ''}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No groups yet. Create one to turn tablets ON.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <GroupFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        group={groupToEdit}
      />

      <Dialog
        open={!!groupToDelete}
        onOpenChange={(open) => {
          if (!open) setGroupToDelete(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete group</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{groupToDelete?.name}"?{' '}
              {memberCountByGroupId.get(groupToDelete?.id || '') || 0} member
              tablet(s) will become ungrouped and report OFF.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setGroupToDelete(null)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
